import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  FlatList,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SIZES, useThemeColors } from '../constants/theme';
import {
  callGetPlaceSuggestions,
  callGetPlaceDetails,
  PlaceSuggestion,
  PlaceDetails,
} from '../services/cloudFunctions';
import { useUserLocation } from '../hooks/useUserLocation';
import { useAppStore } from '../store/useAppStore';
import { direccionCorta } from '../utils/direccion';

/**
 * Autocomplete de direcciones con Google Places.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * REGLA CENTRAL DE ESTE COMPONENTE (la razón de su reescritura):
 * LO QUE EL PADRE TIENE ES SIEMPRE LO QUE SE LEE EN LA PANTALLA.
 *
 * Antes, `onPlaceSelected` solo se disparaba al TOCAR una sugerencia de
 * Google. Eso producía dos fallos medidos en producción:
 *
 *   1. El destino escrito a mano NUNCA llegaba al padre. Si Google no
 *      respondía (sin señal, cuota agotada, o un usuario invitado — la
 *      función `getPlaceSuggestions` exige sesión), el pasajero no podía
 *      pedir un viaje jamás: el único mensaje era «Destino requerido» y no
 *      había forma de salir de ahí.
 *   2. Si elegía una sugerencia y DESPUÉS editaba el texto, el padre se
 *      quedaba con la dirección y las coordenadas anteriores: la pantalla
 *      decía «Los Alcarrizos» y el viaje salía a «Agora Mall». Fallo de
 *      confianza grave.
 *
 * Ahora se emite en CADA cambio de texto. El texto libre viaja con
 * `lat/lng = null`: un destino sin punto en el mapa es correcto y la app ya
 * lo soporta; una coordenada inventada mandaría al chofer a otro sitio, así
 * que NUNCA se rellena a ojo.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Llama al proxy server-side `getPlaceSuggestions` mientras el usuario
 * escribe (debounce 250ms). Cuando selecciona una sugerencia, llama a
 * `getPlaceDetails` con el mismo sessionToken para obtener lat/lng exactos.
 *
 * Sesgo: resultados solo R.D. + radio 20km del usuario si tiene GPS.
 *
 * El contrato con el padre NO cambió: sigue siendo `onPlaceSelected(place)`
 * con la misma forma `PlaceDetails`. Lo que cambió es CUÁNDO se llama.
 */

interface Props {
  placeholder?: string;
  initialValue?: string;
  /**
   * Se llama con el destino vigente cada vez que cambia:
   * - al elegir una sugerencia → `lat`/`lng` con coordenadas reales
   * - al escribir a mano       → `lat`/`lng` en `null` y `placeId` vacío
   * El padre debe tratar `lat === null` como «sin punto en el mapa», no como
   * error: más de la mitad de los viajes reales son así.
   */
  onPlaceSelected: (place: PlaceDetails) => void;
  /** Auto-focus al montar */
  autoFocus?: boolean;
  /**
   * ¿Se puede confirmar el texto escrito a mano como dirección válida?
   *
   * Por defecto SÍ (es la salida de emergencia del campo de DESTINO cuando
   * Google no responde: sin ella, el pasajero se quedaba sin poder pedir un
   * viaje). Pero el campo de RECOGIDA tiene que decir que NO: un origen sin
   * coordenadas hace que `dispatchRide` haga early-return y el viaje quede
   * INVISIBLE para todos los choferes. Ahí no hay salida de emergencia que
   * valga — hay que elegir una sugerencia.
   */
  permitirTextoLibre?: boolean;
}

/**
 * Estado del destino, como máquina de estados explícita. Antes esto vivía en
 * un solo booleano `hasSelected` que mezclaba dos cosas distintas («no
 * busques más» y «hay coordenadas»), y por eso una dirección restaurada se
 * anunciaba como validada por GPS sin serlo.
 */
type EstadoDestino =
  | 'inicial'      // montado con initialValue y aún sin tocar: el padre ya sabe lo que tiene
  | 'escribiendo'  // el usuario está escribiendo: buscamos sugerencias
  | 'google'       // eligió una sugerencia: hay coordenadas exactas
  | 'texto';       // confirmó su propio texto: sin coordenadas, y está bien

export default function PlacesAutocomplete({
  placeholder = 'Buscar dirección…',
  initialValue = '',
  onPlaceSelected,
  autoFocus = false,
  permitirTextoLibre = true,
}: Props) {
  const colors = useThemeColors();
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [estado, setEstado] = useState<EstadoDestino>(
    initialValue.trim().length > 0 ? 'inicial' : 'escribiendo'
  );
  // Por qué falló la búsqueda, para poder decirlo con palabras distintas:
  // quedarse callado es lo que dejaba al pasajero encallado.
  const [fallo, setFallo] = useState<null | 'sesion' | 'red'>(null);
  const debounceTimer = useRef<any>(null);
  const userLoc = useUserLocation();

  // Sin cuenta, `getPlaceSuggestions` responde `unauthenticated` siempre. Lo
  // sabemos ANTES de llamar, así que ni gastamos la llamada ni le mostramos
  // un fallo de red que no es tal: le decimos qué hacer y lo dejamos seguir.
  const invitado = useAppStore((s) => s.guestPreview === true && !s.userFirebaseUid);

  // Session token único por flujo de búsqueda (autocomplete → details).
  // Google factura el flujo completo como 1 transacción si pasas el mismo
  // token a ambos calls. Se regenera al seleccionar (nueva sesión).
  const [sessionToken, setSessionToken] = useState(() => generateSessionToken());

  // El padre pasa un callback inline (cambia de identidad en cada render del
  // mapa). Guardarlo en una ref permite emitir desde temporizadores y efectos
  // sin volver a montarlos ni arrastrar closures viejas.
  const alSeleccionar = useRef(onPlaceSelected);
  useEffect(() => {
    alSeleccionar.current = onPlaceSelected;
  });

  // Última dirección entregada al padre. Evita repetir la misma emisión (el
  // padre hace vibrar el teléfono y re-renderiza el mapa en cada llamada).
  const ultimoEmitido = useRef<string>(initialValue.trim());

  /**
   * EL CAMPO SE SINCRONIZA CON EL PADRE MIENTRAS NADIE HAYA ESCRITO NADA.
   *
   * `initialValue` se leía UNA SOLA VEZ, al montar. El campo de RECOGIDA se
   * monta vacío y su dirección llega ~1,2 s después (geocodificación inversa
   * del GPS): sin esto, ese campo salía VACÍO para siempre y había que
   * escribirse la propia casa a mano cada vez.
   *
   * NO se resuelve con `key={}` para remontar el componente: eso pierde el
   * foco del teclado a media escritura y regenera el testigo de sesión de
   * Google, que es lo que hace que Places facture el flujo entero como una
   * sola transacción.
   *
   * La condición es "nadie ha escrito": el estado 'inicial' (montado con
   * valor) o 'escribiendo' con la caja todavía vacía (montado sin valor). En
   * cuanto hay una letra en la caja, esto deja de tocarla — el texto de la
   * persona manda siempre.
   */
  useEffect(() => {
    const nadieHaEscrito = estado === 'inicial' || (estado === 'escribiendo' && query.length === 0);
    if (!nadieHaEscrito) return;
    const entrante = initialValue ?? '';
    if (entrante.trim() === query.trim()) return;
    setQuery(entrante);
    // El padre YA tiene este valor (nos lo acaba de dar): re-emitirlo sería un
    // bucle de emisiones y una vibración por cada una.
    ultimoEmitido.current = entrante.trim();
    // Y pasa a 'inicial', que es lo que significa: "montado con valor y aún sin
    // tocar". Sin esto, un campo que se rellena solo se quedaba en
    // 'escribiendo' con texto dentro, y el buscador salía a pedirle sugerencias
    // a Google por una dirección que nadie escribió: una llamada facturada y un
    // desplegable abierto encima del campo de abajo, sin que nadie lo pidiera.
    if (entrante.trim().length > 0) setEstado('inicial');
  }, [initialValue, estado, query]);

  /**
   * Entrega al padre una dirección CON coordenadas (vino de Google), ya sin el
   * Plus Code ni el país. Se limpia AQUÍ porque este es el embudo por donde
   * pasan los tres campos que usan el buscador —recogida, destino y «mis
   * direcciones»—: antes solo limpiaba la recogida automática, así que el mismo
   * campo enseñaba una cosa u otra según si la dirección la puso el GPS o la
   * eligió la persona de la lista.
   *
   * Con coordenadas puestas, limpiar el texto no tiene coste: el servidor no
   * vuelve a resolverlo (`resolverDestinoDelViaje` y `resolverDestinoSinPunto`
   * salen antes si el destino ya trae punto). Por eso `emitirTexto`, que es el
   * camino SIN coordenadas, se queda tal cual.
   *
   * Devuelve el texto limpio para que el campo enseñe exactamente lo mismo que
   * se le entregó al formulario.
   */
  const emitirLugar = (place: PlaceDetails): string => {
    const corta = direccionCorta(place.formattedAddress || place.displayName || '');
    ultimoEmitido.current = corta;
    alSeleccionar.current({ ...place, formattedAddress: corta });
    return corta;
  };

  /**
   * Entrega al padre el texto TAL CUAL, sin coordenadas.
   * `lat`/`lng` en null a propósito: es preferible un destino sin punto en el
   * mapa que un punto equivocado.
   */
  const emitirTexto = (texto: string) => {
    const limpio = texto.trim();
    if (ultimoEmitido.current === limpio) return; // nada nuevo que contar
    ultimoEmitido.current = limpio;
    alSeleccionar.current({
      placeId: '',
      formattedAddress: limpio,
      displayName: limpio,
      lat: null,
      lng: null,
    });
  };

  // Debounced fetch (250ms tras dejar de escribir).
  // Solo buscamos mientras el usuario escribe: si ya eligió sugerencia o
  // confirmó su texto, buscar de nuevo reabriría el dropdown "fantasma".
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (estado !== 'escribiendo') {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    if (query.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      setFallo(null);
      return;
    }
    if (invitado) {
      setSuggestions([]);
      setShowDropdown(false);
      setFallo('sesion');
      return;
    }
    const DEBOUNCE_MS = 250;
    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await callGetPlaceSuggestions({
          query: query.trim(),
          sessionToken,
          userLat: userLoc?.lat,
          userLng: userLoc?.lng,
        });
        setSuggestions(resp.data?.suggestions ?? []);
        setShowDropdown(true);
        setFallo(null);
      } catch (e: any) {
        // Antes esto era un `console.warn` mudo: el usuario no se enteraba de
        // nada y se quedaba esperando sugerencias que no iban a llegar.
        const codigo = `${e?.code ?? ''} ${e?.message ?? ''}`.toLowerCase();
        const esSesion =
          codigo.includes('unauthenticated') || codigo.includes('permission-denied');
        console.warn('[PlacesAutocomplete]', e?.message);
        setSuggestions([]);
        setShowDropdown(false);
        setFallo(esSesion ? 'sesion' : 'red');
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [query, sessionToken, userLoc?.lat, userLoc?.lng, estado, invitado]);

  const handleSelect = async (s: PlaceSuggestion) => {
    setLoading(true);
    setShowDropdown(false);
    setFallo(null);
    setEstado('google');
    try {
      const det = await callGetPlaceDetails({
        placeId: s.placeId,
        sessionToken, // mismo token → descuento Google
      });
      const detalle = det.data;
      // Si `getPlaceDetails` responde sin coordenadas, esto NO es una
      // dirección fijada en el mapa: se degrada a texto para no prometer un
      // punto que no existe.
      if (detalle && typeof detalle.lat === 'number' && typeof detalle.lng === 'number') {
        // El campo y el formulario, con el MISMO texto: si no, el campo
        // enseñaría el país y el viaje se guardaría con otra cosa.
        setQuery(emitirLugar(detalle) || s.fullText);
      } else {
        setQuery(detalle?.formattedAddress || s.fullText);
        setEstado('texto');
        emitirTexto(detalle?.formattedAddress || s.fullText);
      }
    } catch (e: any) {
      console.warn('[PlacesAutocomplete details]', e?.message);
      // Fallback: nos quedamos con el texto de la sugerencia, sin coordenadas.
      setQuery(s.fullText);
      setEstado('texto');
      emitirTexto(s.fullText);
    } finally {
      setLoading(false);
      // Nueva session para próxima búsqueda
      setSessionToken(generateSessionToken());
    }
  };

  /** El usuario acepta su propio texto como destino. Cierra el dropdown. */
  const usarTextoEscrito = () => {
    setShowDropdown(false);
    setSuggestions([]);
    setEstado('texto');
    emitirTexto(query);
  };

  const clear = () => {
    setQuery('');
    setSuggestions([]);
    setShowDropdown(false);
    setFallo(null);
    setEstado('escribiendo');
    // Borrar el campo también borra el destino del padre. Sin esto, la
    // pantalla quedaba vacía y el viaje seguía saliendo al destino anterior.
    emitirTexto('');
  };

  const textoUtil = query.trim().length >= 3;
  // El botón «Usar este texto» solo tiene sentido mientras el destino aún no
  // está resuelto; si ya hay sugerencia elegida o texto confirmado, estorba.
  // Y en el campo de RECOGIDA no debe existir: sin coordenadas el viaje queda
  // invisible para todos los choferes.
  const puedeConfirmarTexto = permitirTextoLibre && estado === 'escribiendo' && textoUtil;
  // El desplegable solo se ve si hay algo que listar: con cero sugerencias
  // `showDropdown` sigue en true y ocultaba el botón de salida sin tapar nada.
  const desplegableVisible = showDropdown && suggestions.length > 0;
  const sinResultados =
    estado === 'escribiendo' && textoUtil && !loading && !fallo && showDropdown && suggestions.length === 0;

  return (
    <View style={styles.container}>
      <View
        style={[
          styles.inputWrap,
          { borderColor: colors.border, backgroundColor: colors.backgroundElement },
        ]}
      >
        <Ionicons name="location" size={18} color={colors.primary} style={{ marginRight: 8 }} />
        <TextInput
          style={[styles.input, { color: colors.text }]}
          placeholder={placeholder}
          placeholderTextColor={colors.textSecondary}
          value={query}
          onChangeText={(text) => {
            setQuery(text);
            // Editar INVALIDA cualquier selección anterior, y el padre se
            // entera en el acto. Es la única forma de garantizar que jamás se
            // envíe un viaje a un sitio distinto del que se lee en pantalla.
            if (estado !== 'escribiendo') setEstado('escribiendo');
            emitirTexto(text);
          }}
          onSubmitEditing={() => {
            // Con sugerencias a la vista, la tecla «listo» solo CIERRA la lista:
            // antes confirmaba el texto escrito aunque hubiera una sugerencia de
            // Google justo debajo (un viaje sin punto en el mapa más), y tapaba el
            // campo del sector sin forma clara de cerrarla.
            if (desplegableVisible) {
              setShowDropdown(false);
              return;
            }
            // `permitirTextoLibre` también manda aquí: sin esto, la tecla
            // «listo» del teclado se saltaba la prohibición del campo de
            // recogida y confirmaba un origen sin coordenadas. Y si ya se eligió
            // una sugerencia (estado 'google'), bajar el teclado no la deshace.
            if (permitirTextoLibre && textoUtil && estado !== 'google' && estado !== 'texto') usarTextoEscrito();
          }}
          autoFocus={autoFocus}
          autoCorrect={false}
          autoCapitalize="words"
          returnKeyType="done"
        />
        {loading ? (
          <ActivityIndicator size="small" color={colors.primary} style={{ marginLeft: 6 }} />
        ) : query.length > 0 ? (
          <TouchableOpacity
            onPress={clear}
            style={{ padding: 4 }}
            accessibilityRole="button"
            accessibilityLabel="Borrar el destino escrito"
          >
            <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
          </TouchableOpacity>
        ) : null}
      </View>

      {/* ══ ESTADO DEL DESTINO ═══════════════════════════════════════════
          Una sola fila que dice siempre en qué punto está el destino. Nunca
          se queda muda: quedarse callado cuando Google no responde es lo que
          dejaba al pasajero sin poder pedir un viaje. */}
      {estado === 'google' ? (
        <View style={styles.hintRow}>
          <Ionicons
            name="checkmark-circle"
            size={13}
            color={colors.success}
            style={{ marginRight: 4, marginTop: 1 }}
          />
          <Text style={[styles.hintText, { color: colors.success }]}>
            Dirección fijada con punto exacto en el mapa.
          </Text>
        </View>
      ) : estado === 'texto' ? (
        <View style={styles.hintRow}>
          <Ionicons
            name="create-outline"
            size={13}
            color={colors.textSecondary}
            style={{ marginRight: 4, marginTop: 1 }}
          />
          <Text style={[styles.hintText, { color: colors.textSecondary }]}>
            Se enviará tal como lo escribiste, sin punto en el mapa. Incluye calle, número y
            sector para que el chofer sepa a dónde va.
          </Text>
        </View>
      ) : estado === 'escribiendo' ? (
        <View>
          {fallo === 'sesion' ? (
            <View style={styles.hintRow}>
              <Ionicons
                name="alert-circle-outline"
                size={13}
                color={colors.error}
                style={{ marginRight: 4, marginTop: 1 }}
              />
              <Text style={[styles.hintText, { color: colors.text }]}>
                {permitirTextoLibre
                  ? 'Las sugerencias de direcciones necesitan una cuenta. Puedes seguir sin ellas: escribe calle, número y sector, y toca «Usar este texto».'
                  : 'Las sugerencias de direcciones necesitan una cuenta. Este campo necesita un punto exacto en el mapa: inicia sesión para poder elegirlo.'}
              </Text>
            </View>
          ) : fallo === 'red' ? (
            <View style={styles.hintRow}>
              <Ionicons
                name="cloud-offline-outline"
                size={13}
                color={colors.error}
                style={{ marginRight: 4, marginTop: 1 }}
              />
              <Text style={[styles.hintText, { color: colors.text }]}>
                {permitirTextoLibre
                  ? 'No pudimos conectar con el buscador de direcciones. No te quedes esperando: escribe calle, número y sector, y toca «Usar este texto».'
                  : 'No pudimos conectar con el buscador de direcciones. Este campo necesita un punto exacto en el mapa: revisa tu conexión e inténtalo de nuevo.'}
              </Text>
            </View>
          ) : sinResultados ? (
            <View style={styles.hintRow}>
              <Ionicons
                name="search-outline"
                size={13}
                color={colors.textSecondary}
                style={{ marginRight: 4, marginTop: 1 }}
              />
              <Text style={[styles.hintText, { color: colors.textSecondary }]}>
                {permitirTextoLibre
                  ? 'Google no conoce ese lugar. Puedes continuar con lo que escribiste.'
                  : 'Google no conoce ese lugar. Prueba con una referencia cercana: sin punto exacto en el mapa, ningún chofer puede encontrarlo.'}
              </Text>
            </View>
          ) : (
            <View style={styles.hintRow}>
              <Ionicons
                name="information-circle-outline"
                size={13}
                color={colors.textSecondary}
                style={{ marginRight: 4, marginTop: 1 }}
              />
              <Text style={[styles.hintText, { color: colors.textSecondary }]}>
                {permitirTextoLibre
                  ? 'Elige una sugerencia y la app fija la dirección exacta (calle y número). Si tu destino no aparece, escríbelo completo y continúa igual.'
                  : 'Elige una sugerencia de la lista. Esta dirección necesita un punto exacto en el mapa para que el chofer pueda llegar.'}
              </Text>
            </View>
          )}

          {/* La salida que no existía: aceptar el texto propio como destino. */}
          {puedeConfirmarTexto && !desplegableVisible && (
            <TouchableOpacity
              onPress={usarTextoEscrito}
              style={[styles.botonTexto, { borderColor: colors.primary }]}
              accessibilityRole="button"
              accessibilityLabel={`Usar como destino el texto escrito: ${query.trim()}`}
              activeOpacity={0.7}
            >
              <Ionicons name="arrow-forward-circle-outline" size={15} color={colors.primary} />
              <Text style={[styles.botonTextoLabel, { color: colors.primary }]} numberOfLines={1}>
                Usar este texto: “{query.trim()}”
              </Text>
            </TouchableOpacity>
          )}
        </View>
      ) : null}

      {desplegableVisible && (
        <View
          style={[styles.dropdown, { backgroundColor: colors.card, borderColor: colors.border }]}
        >
          <FlatList
            data={suggestions}
            keyboardShouldPersistTaps="handled"
            // La hoja del pasajero ya es un ScrollView; en Android, sin esto el
            // gesto lo roba la hoja y la lista de sugerencias no se desplaza.
            nestedScrollEnabled
            keyExtractor={(item) => item.placeId}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={styles.item}
                onPress={() => handleSelect(item)}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={`Usar ${item.fullText}`}
              >
                <Ionicons
                  name="location-outline"
                  size={18}
                  color={colors.primary}
                  style={{ marginRight: 10 }}
                />
                <View style={{ flex: 1 }}>
                  <Text style={[styles.itemMain, { color: colors.text }]} numberOfLines={1}>
                    {item.mainText}
                  </Text>
                  {item.secondaryText ? (
                    <Text
                      style={[styles.itemSecondary, { color: colors.textSecondary }]}
                      numberOfLines={1}
                    >
                      {item.secondaryText}
                    </Text>
                  ) : null}
                </View>
              </TouchableOpacity>
            )}
            ItemSeparatorComponent={() => (
              <View style={[styles.separator, { backgroundColor: colors.border }]} />
            )}
          />
          {/* La misma salida, también con el desplegable abierto: el desplegable
              tapa la fila de estado, así que sin esta fila el botón «Usar este
              texto» sería invisible justo cuando Google ofrece sugerencias
              parecidas pero ninguna correcta.

              ABAJO Y FIJA (17-sep-2026). Era la cabecera de la lista: la PRIMERA
              fila, encima de las sugerencias de Google y con su mismo aspecto.
              Quien escribía «Megacentro» y tocaba «el primer resultado» elegía
              esta, y el viaje salía SIN punto en el mapa: tras el PIN el chofer
              no tenía cómo navegar (el fallo que reportó el fundador, medido en
              producción). Como pie de la LISTA quedaba fuera de la vista con 5
              sugerencias (alto máximo 320); fuera de la lista, la lista se
              encoge (flexShrink) y esta fila queda siempre dentro del
              desplegable. En la hoja del pasajero puede caer por debajo de lo
              visible: la tecla «listo» cierra la lista y deja a la vista el
              botón «Usar este texto» y el campo del sector. */}
          {puedeConfirmarTexto ? (
            <TouchableOpacity
              style={[styles.item, { borderTopWidth: 1, borderTopColor: colors.border }]}
              onPress={usarTextoEscrito}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel={`Usar como destino el texto escrito: ${query.trim()}`}
            >
              <Ionicons
                name="create-outline"
                size={18}
                color={colors.primary}
                style={{ marginRight: 10 }}
              />
              <View style={{ flex: 1 }}>
                <Text style={[styles.itemMain, { color: colors.primary }]} numberOfLines={1}>
                  Usar “{query.trim()}”
                </Text>
                <Text style={[styles.itemSecondary, { color: colors.textSecondary }]}>
                  Tal como lo escribiste, sin punto en el mapa
                </Text>
              </View>
            </TouchableOpacity>
          ) : null}
          <View style={[styles.attribution, { borderTopColor: colors.border }]}>
            <Text style={[styles.attributionText, { color: colors.textSecondary }]}>
              Sugerencias por Google
            </Text>
          </View>
        </View>
      )}
    </View>
  );
}

function generateSessionToken(): string {
  // UUID v4 sin dependencia externa — sirve para Google Places sessionToken
  const hex = '0123456789abcdef';
  let id = '';
  for (let i = 0; i < 32; i++) {
    if (i === 8 || i === 12 || i === 16 || i === 20) id += '-';
    if (i === 12) id += '4';
    else if (i === 16) id += hex[8 + Math.floor(Math.random() * 4)];
    else id += hex[Math.floor(Math.random() * 16)];
  }
  return id;
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 10,
  },
  inputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 4,
  },
  input: {
    flex: 1,
    fontSize: 14,
    paddingVertical: 12,
    outlineStyle: 'none' as any,
  },
  hintRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 4,
    paddingTop: 6,
  },
  hintText: {
    flex: 1,
    fontSize: 11,
    lineHeight: 15,
  },
  botonTexto: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 10,
    // Alto cómodo para el dedo: el objetivo táctil mínimo son 44px y esta es
    // la salida de emergencia cuando Google no responde.
    paddingVertical: 10,
    marginTop: 8,
    marginHorizontal: 4,
    maxWidth: '100%',
  },
  botonTextoLabel: {
    flexShrink: 1,
    marginLeft: 6,
    fontSize: SIZES.small,
    fontWeight: '700',
  },
  dropdown: {
    position: 'absolute',
    top: 56,
    left: 0,
    right: 0,
    borderRadius: 12,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
    maxHeight: 320,
    zIndex: 100,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
  },
  itemMain: {
    fontSize: 14,
    fontWeight: '600',
  },
  itemSecondary: {
    fontSize: 12,
    marginTop: 2,
  },
  separator: {
    height: 1,
    marginLeft: 40,
  },
  attribution: {
    padding: 8,
    borderTopWidth: 1,
    alignItems: 'flex-end',
  },
  attributionText: {
    fontSize: 10,
    fontStyle: 'italic',
  },
});
